import { useState } from 'react';
/**
 * Nev-monogram szinezett hattéren.
 *
 * A szin determinisztikus a felhasznalo azonositojabol - ugyanaz a kollega
 * mindig ugyanolyan szinu, igy a szem hamarabb megtalalja a folyamban, mint
 * ahogy a nevet elolvasna. A paletta halk, hogy sok avatar egyutt se legyen zajos.
 */
const PALETTE = [
  { bg: 'oklch(88% 0.055 45)', ink: 'oklch(38% 0.09 45)' }, // agyag
  { bg: 'oklch(88% 0.05 155)', ink: 'oklch(36% 0.08 155)' }, // zsalya
  { bg: 'oklch(88% 0.05 224)', ink: 'oklch(36% 0.09 224)' }, // petrol
  { bg: 'oklch(89% 0.05 78)', ink: 'oklch(38% 0.08 78)' }, // homok
  { bg: 'oklch(88% 0.045 300)', ink: 'oklch(38% 0.08 300)' }, // levendula
  { bg: 'oklch(88% 0.05 12)', ink: 'oklch(38% 0.09 12)' }, // rozsa
  { bg: 'oklch(88% 0.05 195)', ink: 'oklch(36% 0.08 195)' }, // jegkek
];

const SIZES = {
  /** Apró, egymásra csúsztatható – a „látta" sor. */
  xs: 'size-4 text-[7px] rounded-full',
  sm: 'size-6 text-[10px] rounded-md',
  md: 'size-9 text-xs rounded-[0.6rem]',
  lg: 'size-12 text-sm rounded-xl',
  /** Kitolti a szulojet - a huddle-csempek hasznaljak. */
  fill: 'size-full text-4xl rounded-none',
  /** Kitolti a szulojet, kis monogrammal - a megosztas alatti kis csempek. */
  fillSm: 'size-full text-base rounded-none',
} as const;

function hashOf(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  return hash;
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function Avatar({
  userId,
  name,
  avatarUrl,
  size = 'md',
  className = '',
}: {
  userId: string;
  name: string;
  /** Ha van feltoltott profilkep, az nyer a monogram helyett. */
  avatarUrl?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const color = PALETTE[hashOf(userId) % PALETTE.length]!;
  const [failed, setFailed] = useState(false);

  /**
   * A kep betoltesi hibaja NEM hagy ures helyet.
   *
   * Ha a fajl eltunt vagy a halozat akad, visszaesunk a monogramra - az
   * mindig megjelenik, es a determinisztikus szin miatt igy is felismerheto
   * a kollega.
   */
  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        onError={() => setFailed(true)}
        style={{ background: color.bg }}
        className={`shrink-0 object-cover ${SIZES[size]} ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{ background: color.bg, color: color.ink }}
      className={`grid shrink-0 place-items-center font-semibold ${SIZES[size]} ${className}`}
    >
      {initialsOf(name) || '?'}
    </span>
  );
}
